import { defineComponent } from "../lib/component.ts";

interface PreLaunchRefs {
  scroller?: HTMLElement;

  roomName?: HTMLElement;

  roomLocation?: HTMLElement;

  roomIndex?: HTMLElement;

  countdown?: HTMLElement;

  days?: HTMLElement;

  hours?: HTMLElement;

  minutes?: HTMLElement;

  seconds?: HTMLElement;

  progress?: HTMLElement;

  progressFill?: HTMLElement;

  playPause?: HTMLButtonElement;

  playIcon?: HTMLElement;

  pauseIcon?: HTMLElement;

  mute?: HTMLButtonElement;

  audioOnLabel?: HTMLElement;

  audioOffLabel?: HTMLElement;
}

const PORTRAIT_QUERY = "(aspect-ratio < 1/1)";

interface VideoAudioFlags {
  audioTracks?: ArrayLike<unknown>;
  mozHasAudio?: boolean;
  webkitAudioDecodedByteCount?: number;
}

function videoAudioFlags(video: HTMLVideoElement): VideoAudioFlags {
  const tracks: unknown = Reflect.get(video, "audioTracks");
  const mozHasAudio: unknown = Reflect.get(video, "mozHasAudio");
  const webkitBytes: unknown = Reflect.get(video, "webkitAudioDecodedByteCount");
  const flags: VideoAudioFlags = {};
  if (tracks !== null && typeof tracks === "object" && "length" in tracks) {
    const length = Reflect.get(tracks, "length");
    if (typeof length === "number") {
      flags.audioTracks = { length };
    }
  }
  if (typeof mozHasAudio === "boolean") flags.mozHasAudio = mozHasAudio;
  if (typeof webkitBytes === "number") flags.webkitAudioDecodedByteCount = webkitBytes;
  return flags;
}

class PreLaunchGallery extends defineComponent<PreLaunchRefs>()(["scroller"]) {
  #slots: HTMLElement[] = [];

  #slotRealIndex = new Map<HTMLElement, number>();

  #uniqueCount = 0;

  #advanceTimer: number | null = null;

  #progressRaf: number | null = null;

  #progressStart = 0;

  #countdownTimer: number | null = null;

  #observer: IntersectionObserver | null = null;

  #activeSlot: HTMLElement | null = null;

  #activeVideo: HTMLVideoElement | null = null;

  #activatedVideos = new Set<HTMLVideoElement>();

  #visibleSlots = new Set<HTMLElement>();

  #audioIntent = false;

  #autoplay = true;

  #reducedMotion = false;

  #suppressScrollReset = false;

  #userScrolling = false;

  #userScrollStartSlot: HTMLElement | null = null;

  #lastUserGestureAt = 0;

  #scrollEndFallbackTimer: number | null = null;

  #hasNativeScrollEnd = "onscrollend" in window;

  #portraitQuery: MediaQueryList = window.matchMedia(PORTRAIT_QUERY);

  #videoController: AbortController | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    if (window.Shopify?.designMode) return;

    const scroller = this.refs.scroller;

    const originals: HTMLElement[] = [];
    for (const el of scroller.children) {
      if (el instanceof HTMLElement && el.querySelector(".pre-launch-room")) {
        originals.push(el);
      }
    }
    if (originals.length === 0) return;

    this.#reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.#autoplay = !this.#reducedMotion;
    this.#uniqueCount = originals.length;

    this.#initControls();
    this.#startCountdown();

    document.addEventListener("visibilitychange", this.#onVisibilityChange, {
      signal: this.signal,
    });
    this.#portraitQuery.addEventListener("change", this.#onAspectRatioChange, {
      signal: this.signal,
    });

    if (originals.length === 1) {
      this.#slots = originals;
      this.#slotRealIndex.set(originals[0], 0);
      this.#visibleSlots.add(originals[0]);
      this.#activateSlot(originals[0]);
      this.#playSlot(originals[0]);
      return;
    }

    this.#slots = this.#buildSlots(originals);
    this.#preRotate();
    for (const slot of this.#slots) slot.inert = true;

    const starting = this.#slots[1];
    this.#suppressScrollReset = true;
    scroller.scrollTo({ top: starting.offsetTop, behavior: "instant" });
    requestAnimationFrame(() => {
      this.#suppressScrollReset = false;
    });

    this.#activateSlot(starting);
    this.#observeSlots();

    scroller.addEventListener("scroll", this.#onScroll, {
      passive: true,
      signal: this.signal,
    });
    if (this.#hasNativeScrollEnd) {
      scroller.addEventListener("scrollend", this.#onScrollEnd, {
        signal: this.signal,
      });
    }
    scroller.addEventListener("wheel", this.#onUserInteraction, {
      passive: true,
      signal: this.signal,
    });
    scroller.addEventListener("touchstart", this.#onUserInteraction, {
      passive: true,
      signal: this.signal,
    });
    scroller.addEventListener("keydown", this.#onUserInteraction, {
      signal: this.signal,
    });

    if (!this.#reducedMotion) {
      this.#startAutoAdvance();
    }
  }

  disconnectedCallback(): void {
    this.#stopAutoAdvance();
    this.#stopProgress();
    this.#stopCountdown();
    if (this.#scrollEndFallbackTimer !== null) {
      clearTimeout(this.#scrollEndFallbackTimer);
      this.#scrollEndFallbackTimer = null;
    }
    this.#observer?.disconnect();
    this.#observer = null;
    for (const video of Array.from(this.#activatedVideos)) this.#deactivateVideo(video);
    this.#activatedVideos.clear();
    super.disconnectedCallback();
  }

  #buildSlots(originals: HTMLElement[]): HTMLElement[] {
    const scroller = this.refs.scroller;
    for (let i = 0; i < originals.length; i++) {
      this.#slotRealIndex.set(originals[i], i);
    }
    if (originals.length !== 2) return originals;
    const clones: HTMLElement[] = [];
    for (let i = 0; i < originals.length; i++) {
      const clone = originals[i].cloneNode(true);
      if (!(clone instanceof HTMLElement)) continue;
      this.#slotRealIndex.set(clone, i);
      scroller.appendChild(clone);
      clones.push(clone);
    }
    return [...originals, ...clones];
  }

  #preRotate(): void {
    if (this.#slots.length < 2) return;
    const scroller = this.refs.scroller;
    const last = this.#slots.pop();
    if (!last) return;
    scroller.insertBefore(last, this.#slots[0] ?? null);
    this.#slots.unshift(last);
  }

  #observeSlots(): void {
    const scroller = this.refs.scroller;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement)) continue;
          const slot = entry.target;
          const wasVisible = this.#visibleSlots.has(slot);
          if (entry.isIntersecting) {
            if (!wasVisible) {
              this.#visibleSlots.add(slot);
              this.#playSlot(slot);
            }
            if (entry.intersectionRatio >= 0.6) {
              this.#activateSlot(slot);
            }
          } else if (wasVisible) {
            this.#visibleSlots.delete(slot);
            this.#stopSlot(slot);
          }
        }
      },
      { root: scroller, threshold: [0, 0.6] },
    );

    for (const slot of this.#slots) this.#observer.observe(slot);
  }

  #activateSlot(slot: HTMLElement): void {
    if (slot === this.#activeSlot) return;
    const previousVideo = this.#activeVideo;
    if (this.#activeSlot) this.#activeSlot.inert = true;
    this.#activeSlot = slot;
    slot.inert = false;

    const article = slot.querySelector(".pre-launch-room");
    const name = article?.getAttribute("data-name") ?? "";
    const location = article?.getAttribute("data-location") ?? "";
    const realIndex = this.#slotRealIndex.get(slot) ?? 0;
    this.#updateRoomLabel(name, location, realIndex);

    const video = this.#matchedVideo(slot);
    this.#setActiveVideo(video);

    if (previousVideo && previousVideo !== video) {
      previousVideo.muted = true;
    }
  }

  #playSlot(slot: HTMLElement): void {
    const video = this.#matchedVideo(slot);
    if (!video) return;
    this.#activateVideo(video);
    try {
      video.currentTime = 0;
    } catch {}
    if (this.#autoplay) {
      const result = video.play();
      if (result && typeof result.catch === "function") {
        result.catch((err: unknown) => this.#onPlayRejected(video, err));
      }
    }
  }

  #stopSlot(slot: HTMLElement): void {
    const video = this.#matchedVideo(slot);
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {}
  }

  #matchedVideo(slot: HTMLElement): HTMLVideoElement | null {
    const variant = this.#portraitQuery.matches ? "portrait" : "landscape";
    const video = slot.querySelector(`video[data-variant="${variant}"]`);
    return video instanceof HTMLVideoElement ? video : null;
  }

  #activateVideo(video: HTMLVideoElement): void {
    if (this.#activatedVideos.has(video)) return;
    const template = video.querySelector("template");
    if (template instanceof HTMLTemplateElement) {
      const sources = template.content.querySelectorAll("source");
      for (const source of sources) video.appendChild(source.cloneNode(true));
      template.remove();
      video.load();
    }
    const poster = this.#siblingPoster(video);
    if (poster) {
      poster.setAttribute("fetchpriority", "high");
      poster.setAttribute("loading", "eager");
      video.addEventListener(
        "playing",
        () => {
          poster.style.display = "none";
        },
        { signal: this.signal },
      );
    }
    this.#activatedVideos.add(video);
  }

  #deactivateVideo(video: HTMLVideoElement): void {
    video.pause();
    const sources = video.querySelectorAll("source");
    if (sources.length > 0) {
      const template = document.createElement("template");
      for (const source of Array.from(sources)) template.content.appendChild(source);
      video.appendChild(template);
      video.removeAttribute("src");
      video.load();
    }
    const poster = this.#siblingPoster(video);
    if (poster) poster.style.removeProperty("display");
    this.#activatedVideos.delete(video);
  }

  #siblingPoster(video: HTMLVideoElement): HTMLImageElement | null {
    const article = video.closest(".pre-launch-room");
    const variant = video.dataset.variant;
    if (!article || !variant) return null;
    const img = article.querySelector(`img[data-variant="${variant}"]`);
    return img instanceof HTMLImageElement ? img : null;
  }

  #setActiveVideo(video: HTMLVideoElement | null): void {
    if (this.#activeVideo === video) return;
    this.#teardownActiveVideoListeners();
    this.#activeVideo = video;
    const { mute } = this.refs;
    if (mute) mute.hidden = true;
    if (!video) {
      this.#syncPlayButton(false);
      return;
    }
    this.#activateVideo(video);
    video.muted = !this.#audioIntent;
    this.#bindActiveVideoListeners(video);
    if (!this.#autoplay && !video.paused) video.pause();
    this.#syncPlayButton(!video.paused);
    this.#syncMuteButton(video.muted);
    this.#detectAudioFor(video);
  }

  #bindActiveVideoListeners(video: HTMLVideoElement): void {
    this.#videoController = new AbortController();
    const { signal } = this.#videoController;
    video.addEventListener("play", this.#onVideoPlay, { signal });
    video.addEventListener("pause", this.#onVideoPause, { signal });
    video.addEventListener("volumechange", this.#onVideoVolumeChange, { signal });
  }

  #teardownActiveVideoListeners(): void {
    this.#videoController?.abort();
    this.#videoController = null;
  }

  #onVideoPlay = (): void => {
    this.#syncPlayButton(true);
  };

  #onVideoPause = (): void => {
    this.#syncPlayButton(false);
  };

  #onVideoVolumeChange = (): void => {
    if (!this.#activeVideo) return;
    this.#syncMuteButton(this.#activeVideo.muted);
  };

  #detectAudioFor(video: HTMLVideoElement): void {
    const check = (): void => {
      if (video !== this.#activeVideo) return;
      const flags = videoAudioFlags(video);
      const hasAudio =
        (flags.audioTracks?.length ?? 0) > 0 ||
        flags.mozHasAudio === true ||
        (flags.webkitAudioDecodedByteCount ?? 0) > 0;
      const { playPause, mute } = this.refs;
      if (playPause) playPause.hidden = false;
      if (mute) mute.hidden = !hasAudio;
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      check();
    } else {
      video.addEventListener("loadeddata", check, { once: true, signal: this.signal });
    }
  }

  #playActiveVideo(): void {
    const video = this.#activeVideo;
    if (!video) return;
    const result = video.play();
    if (result && typeof result.catch === "function") {
      result.catch((err: unknown) => this.#onPlayRejected(video, err));
    }
  }

  #onPlayRejected(video: HTMLVideoElement, error: unknown): void {
    const name = error instanceof DOMException ? error.name : null;
    if (name !== "NotAllowedError" && name !== "AbortError") {
      const mediaError = video.error;
      console.warn("[pre-launch] video.play() rejected", {
        name,
        message: error instanceof Error ? error.message : String(error),
        mediaErrorCode: mediaError?.code,
        mediaErrorMessage: mediaError?.message,
        currentSrc: video.currentSrc,
      });
    }
    if (name !== "NotAllowedError") return;
    this.#autoplay = false;
    const { playPause } = this.refs;
    if (playPause) playPause.hidden = false;
    const activeVideo = this.#activeVideo;
    const isPlaying = Boolean(activeVideo && !activeVideo.paused && !activeVideo.ended);
    this.#syncPlayButton(isPlaying);
  }

  #syncPlayButton(isPlaying: boolean): void {
    const { playPause, playIcon, pauseIcon } = this.refs;
    if (!playPause) return;
    if (playIcon) playIcon.hidden = isPlaying;
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
    const label = isPlaying ? playPause.dataset.labelPause : playPause.dataset.labelPlay;
    if (label) playPause.setAttribute("aria-label", label);
  }

  #syncMuteButton(isMuted: boolean): void {
    const { mute, audioOnLabel, audioOffLabel } = this.refs;
    if (!mute) return;
    if (audioOnLabel) audioOnLabel.hidden = isMuted;
    if (audioOffLabel) audioOffLabel.hidden = !isMuted;
    const label = isMuted ? mute.dataset.labelUnmute : mute.dataset.labelMute;
    if (label) mute.setAttribute("aria-label", label);
  }

  #initControls(): void {
    this.refs.playPause?.addEventListener("click", this.#onPlayPauseClick, {
      signal: this.signal,
    });
    this.refs.mute?.addEventListener("click", this.#onMuteClick, {
      signal: this.signal,
    });
  }

  #onPlayPauseClick = (): void => {
    const video = this.#activeVideo;
    if (!video) return;
    if (video.paused) {
      this.#autoplay = true;
      this.#playActiveVideo();
    } else {
      this.#autoplay = false;
      video.pause();
    }
  };

  #onMuteClick = (): void => {
    this.#audioIntent = !this.#audioIntent;
    const video = this.#activeVideo;
    if (video) video.muted = !this.#audioIntent;
  };

  #onAspectRatioChange = (): void => {
    for (const video of Array.from(this.#activatedVideos)) {
      this.#deactivateVideo(video);
    }

    if (this.#activeSlot) {
      const next = this.#matchedVideo(this.#activeSlot);
      this.#setActiveVideo(next);
    }
    for (const slot of this.#visibleSlots) {
      this.#playSlot(slot);
    }
  };

  #updateRoomLabel(name: string, location: string, index: number): void {
    const { roomName, roomLocation, roomIndex } = this.refs;
    if (!roomName || !roomLocation) return;
    const total = this.#uniqueCount;
    const label = index >= 0 && total > 0 ? `${String(index + 1).padStart(2, "0")}` : "";
    roomName.textContent = name;
    roomLocation.textContent = location;
    if (roomIndex) roomIndex.textContent = label;
  }

  #onScroll = (): void => {
    if (performance.now() - this.#lastUserGestureAt < 500) {
      if (!this.#userScrolling) this.#userScrollStartSlot = this.#activeSlot;
      this.#userScrolling = true;
    }
    if (!this.#hasNativeScrollEnd) {
      if (this.#scrollEndFallbackTimer !== null) clearTimeout(this.#scrollEndFallbackTimer);
      this.#scrollEndFallbackTimer = window.setTimeout(() => {
        this.#scrollEndFallbackTimer = null;
        this.#onScrollEnd();
      }, 150);
    }
  };

  #onScrollEnd = (): void => {
    if (this.#suppressScrollReset) return;
    const wasUserScrolling = this.#userScrolling;
    const startSlot = this.#userScrollStartSlot;
    this.#userScrolling = false;
    this.#userScrollStartSlot = null;
    if (this.#slots.length >= 2) {
      const active = this.#activeSlot;
      if (active) {
        const index = this.#slots.indexOf(active);
        if (index <= 0) {
          this.#rotate(-1);
        } else if (index >= this.#slots.length - 1) {
          this.#rotate(1);
        }
      }
    }
    if (wasUserScrolling && !this.#reducedMotion && this.#activeSlot !== startSlot) {
      this.#restartAutoAdvance();
    }
  };

  #rotate(direction: 1 | -1): void {
    const active = this.#activeSlot;
    if (!active) return;
    const scroller = this.refs.scroller;
    this.#suppressScrollReset = true;
    if (direction > 0) {
      const first = this.#slots.shift();
      if (first) {
        scroller.appendChild(first);
        this.#slots.push(first);
      }
    } else {
      const last = this.#slots.pop();
      if (last) {
        scroller.insertBefore(last, this.#slots[0] ?? null);
        this.#slots.unshift(last);
      }
    }
    scroller.scrollTo({ top: active.offsetTop, behavior: "instant" });
    requestAnimationFrame(() => {
      this.#suppressScrollReset = false;
    });
  }

  #onUserInteraction = (): void => {
    if (this.#reducedMotion) return;
    this.#lastUserGestureAt = performance.now();
  };

  #onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      this.#stopAutoAdvance();
      this.#stopProgress();
    } else if (!this.#reducedMotion && this.#slots.length >= 2) {
      this.#startAutoAdvance();
    }
  };

  #startAutoAdvance(): void {
    this.#stopAutoAdvance();
    const interval = this.#intervalMs();
    this.#progressStart = performance.now();
    this.#startProgress();
    this.#advanceTimer = window.setInterval(() => {
      if (this.#userScrolling) return;
      this.#advance();
      this.#progressStart = performance.now();
    }, interval);
  }

  #restartAutoAdvance(): void {
    if (this.#advanceTimer === null && this.#progressRaf === null) return;
    this.#stopAutoAdvance();
    this.#stopProgress();
    this.#startAutoAdvance();
  }

  #stopAutoAdvance(): void {
    if (this.#advanceTimer !== null) {
      clearInterval(this.#advanceTimer);
      this.#advanceTimer = null;
    }
  }

  #advance(): void {
    const scroller = this.refs.scroller;
    scroller.scrollBy({ top: scroller.clientHeight, behavior: "smooth" });
  }

  #startProgress(): void {
    const fill = this.refs.progressFill;
    if (!fill) return;
    const tick = (): void => {
      const elapsed = performance.now() - this.#progressStart;
      const progress = Math.min(1, elapsed / this.#intervalMs());
      fill.style.setProperty("--pre-launch-progress", String(progress));
      this.#progressRaf = requestAnimationFrame(tick);
    };
    this.#progressRaf = requestAnimationFrame(tick);
  }

  #stopProgress(): void {
    if (this.#progressRaf !== null) {
      cancelAnimationFrame(this.#progressRaf);
      this.#progressRaf = null;
    }
    const fill = this.refs.progressFill;
    if (fill) fill.style.setProperty("--pre-launch-progress", "0");
  }

  #intervalMs(): number {
    const n = Number(this.dataset.intervalMs);
    return Number.isFinite(n) && n > 0 ? n : 5000;
  }

  #startCountdown(): void {
    const launchAtMs = this.dataset.launchAtMs;
    if (!launchAtMs) return;
    const target = Number(launchAtMs);
    if (!Number.isFinite(target) || target <= 0) return;

    const update = (): void => {
      const remaining = Math.max(0, target - Date.now());
      this.#renderCountdown(remaining);
      if (remaining <= 0) this.#stopCountdown();
    };

    update();
    if (this.#countdownTimer === null) {
      this.#countdownTimer = window.setInterval(update, 1000);
    }
  }

  #stopCountdown(): void {
    if (this.#countdownTimer !== null) {
      clearInterval(this.#countdownTimer);
      this.#countdownTimer = null;
    }
  }

  #renderCountdown(ms: number): void {
    const { countdown, days, hours, minutes, seconds } = this.refs;
    const totalSeconds = Math.floor(ms / 1000);
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (days) days.textContent = String(d).padStart(2, "0");
    if (hours) hours.textContent = String(h).padStart(2, "0");
    if (minutes) minutes.textContent = String(m).padStart(2, "0");
    if (seconds) seconds.textContent = String(s).padStart(2, "0");
    if (countdown) countdown.hidden = false;
  }
}

if (!customElements.get("pre-launch-gallery")) {
  customElements.define("pre-launch-gallery", PreLaunchGallery);
}
